import { expectArray, expectBoolean, expectRecord, expectString } from "./schema";
import { parseStudentBillingCurrency, parseStudentBillingIdentifier, parseStudentBillingServiceMonth } from "./student-billing";
import { STUDENT_BILLING_COLLECTION_METHODS, STUDENT_BILLING_COLLECTION_SOURCES } from "./student-billing-collection-authority";
export const STUDENT_BILLING_MONTHLY_LINE_SOURCE_TYPES = [
    "regularLesson", "shortTermLesson", "event", "individualManual", "lessonManual"
];
export const STUDENT_BILLING_MONTHLY_WARNING_CODES = [
    "LESSON_FEE_NOT_CONFIGURED",
    "LESSON_FEE_MONTH_AMBIGUOUS",
    "LESSON_ENROLLMENT_AMBIGUOUS",
    "SHORT_TERM_BILLING_MONTH_UNRESOLVED",
    "EVENT_FEE_NOT_CONFIGURED",
    "EVENT_CHARGE_TARGET_NOT_CONFIRMED",
    "EVENT_CHARGE_TARGET_STALE",
    "EVENT_TARGET_STUDENT_INVALID",
    "STUDENT_INACTIVE_WITH_BILLING_SOURCE",
    "MONTHLY_LINE_LIMIT_EXCEEDED",
    "MONTHLY_TOTAL_NEGATIVE",
    "PAYER_ASSIGNMENT_MISSING",
    "PAYER_ASSIGNMENT_OVERLAP",
    "PAYER_ASSIGNMENT_MONTH_AMBIGUOUS",
    "CARD_NOT_READY",
    "PAYMENT_ACCOUNT_NOT_READY"
];
function exact(record, allowed, path) {
    const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
    if (unknown.length)
        throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
}
function enumValue(value, allowed, path) {
    const parsed = expectString(value, path);
    if (!allowed.includes(parsed))
        throw new Error(`${path} is invalid`);
    return parsed;
}
function integer(value, path, minimum) {
    if (!Number.isSafeInteger(value) || (minimum !== undefined && Number(value) < minimum))
        throw new Error(`${path} is invalid`);
    return Number(value);
}
function iso(value, path) {
    const parsed = expectString(value, path);
    if (!Number.isFinite(new Date(parsed).getTime()))
        throw new Error(`${path} is invalid`);
    return new Date(parsed).toISOString();
}
function fingerprint(value, path) {
    const parsed = expectString(value, path);
    if (!/^[a-f0-9]{64}$/u.test(parsed))
        throw new Error(`${path} is invalid`);
    return parsed;
}
function nullableIdentifier(value, path) {
    return value === null ? null : parseStudentBillingIdentifier(value, path);
}
export function parseGenerateStudentBillingMonthlyDraftRequest(value, path = "monthlyDraftGenerateRequest") {
    const record = expectRecord(value, path);
    exact(record, ["expectedGenerationVersion"], path);
    return { expectedGenerationVersion: record.expectedGenerationVersion === null ? null : integer(record.expectedGenerationVersion, `${path}.expectedGenerationVersion`, 1) };
}
export function parsePutStudentBillingEventChargeTargetRequest(value, path = "eventChargeTargetRequest") {
    const record = expectRecord(value, path);
    exact(record, ["studentIds", "expectedTargetFingerprint", "expectedVersion"], path);
    const studentIds = expectArray(record.studentIds, parseStudentBillingIdentifier, `${path}.studentIds`);
    if (studentIds.length > 200 || new Set(studentIds).size !== studentIds.length)
        throw new Error(`${path}.studentIds is invalid`);
    return {
        studentIds: [...studentIds].sort(),
        expectedTargetFingerprint: fingerprint(record.expectedTargetFingerprint, `${path}.expectedTargetFingerprint`),
        expectedVersion: record.expectedVersion === null ? null : integer(record.expectedVersion, `${path}.expectedVersion`, 1)
    };
}
export function parseStudentBillingEventChargeTarget(value, path = "eventChargeTarget") {
    const record = expectRecord(value, path);
    exact(record, ["eventId", "tenantId", "eventRevision", "targetStudentIds", "targetFingerprint", "version", "confirmedByPlatformUserId", "createdAt", "updatedAt"], path);
    const targetStudentIds = expectArray(record.targetStudentIds, parseStudentBillingIdentifier, `${path}.targetStudentIds`);
    if (targetStudentIds.length > 200 || new Set(targetStudentIds).size !== targetStudentIds.length)
        throw new Error(`${path}.targetStudentIds is invalid`);
    return {
        eventId: parseStudentBillingIdentifier(record.eventId, `${path}.eventId`),
        tenantId: parseStudentBillingIdentifier(record.tenantId, `${path}.tenantId`),
        eventRevision: integer(record.eventRevision, `${path}.eventRevision`, 1),
        targetStudentIds,
        targetFingerprint: fingerprint(record.targetFingerprint, `${path}.targetFingerprint`),
        version: integer(record.version, `${path}.version`, 1),
        confirmedByPlatformUserId: parseStudentBillingIdentifier(record.confirmedByPlatformUserId, `${path}.confirmedByPlatformUserId`),
        createdAt: iso(record.createdAt, `${path}.createdAt`),
        updatedAt: iso(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseStudentBillingMonthlyWarning(value, path = "monthlyWarning") {
    const record = expectRecord(value, path);
    exact(record, ["code", "blocking", "sourceType", "sourceId"], path);
    return {
        code: enumValue(record.code, STUDENT_BILLING_MONTHLY_WARNING_CODES, `${path}.code`),
        blocking: expectBoolean(record.blocking, `${path}.blocking`),
        sourceType: enumValue(record.sourceType, [...STUDENT_BILLING_MONTHLY_LINE_SOURCE_TYPES, "student", "payer", "month"], `${path}.sourceType`),
        sourceId: nullableIdentifier(record.sourceId, `${path}.sourceId`)
    };
}
export function parseStudentBillingMonthlyLine(value, path = "monthlyLine") {
    const record = expectRecord(value, path);
    exact(record, ["lineId", "sourceType", "sourceId", "sourceVersion", "evidenceIds", "labelSnapshot", "amount", "currency"], path);
    const evidenceIds = expectArray(record.evidenceIds, parseStudentBillingIdentifier, `${path}.evidenceIds`);
    if (evidenceIds.length === 0 || evidenceIds.length > 12 || new Set(evidenceIds).size !== evidenceIds.length)
        throw new Error(`${path}.evidenceIds is invalid`);
    return {
        lineId: parseStudentBillingIdentifier(record.lineId, `${path}.lineId`),
        sourceType: enumValue(record.sourceType, STUDENT_BILLING_MONTHLY_LINE_SOURCE_TYPES, `${path}.sourceType`),
        sourceId: parseStudentBillingIdentifier(record.sourceId, `${path}.sourceId`),
        sourceVersion: integer(record.sourceVersion, `${path}.sourceVersion`, 1),
        evidenceIds,
        labelSnapshot: expectString(record.labelSnapshot, `${path}.labelSnapshot`),
        amount: integer(record.amount, `${path}.amount`),
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`)
    };
}
export function parseStudentBillingMonthlyPayerPreview(value, path = "payerPreview") {
    const record = expectRecord(value, path);
    exact(record, ["payerId", "payerLabel", "payerAssignmentId", "collectionMethod", "collectionSource", "paymentMethodReady"], path);
    return {
        payerId: nullableIdentifier(record.payerId, `${path}.payerId`),
        payerLabel: record.payerLabel === null ? null : expectString(record.payerLabel, `${path}.payerLabel`),
        payerAssignmentId: nullableIdentifier(record.payerAssignmentId, `${path}.payerAssignmentId`),
        collectionMethod: record.collectionMethod === null ? null : enumValue(record.collectionMethod, STUDENT_BILLING_COLLECTION_METHODS, `${path}.collectionMethod`),
        collectionSource: record.collectionSource === null ? null : enumValue(record.collectionSource, STUDENT_BILLING_COLLECTION_SOURCES, `${path}.collectionSource`),
        paymentMethodReady: record.paymentMethodReady === null ? null : expectBoolean(record.paymentMethodReady, `${path}.paymentMethodReady`)
    };
}
export function parseStudentBillingMonthlyUnit(value, path = "monthlyUnit") {
    const record = expectRecord(value, path);
    exact(record, ["billingUnitId", "tenantId", "studentId", "studentDisplayNameSnapshot", "serviceMonth", "finalizationStatus", "readiness", "currency", "lines", "lessonSubtotal", "eventSubtotal", "manualSubtotal", "totalAmount", "warnings", "payerPreview", "sourceFingerprint", "version", "createdAt", "updatedAt"], path);
    if (record.finalizationStatus !== "draft")
        throw new Error(`${path}.finalizationStatus is invalid`);
    const readiness = enumValue(record.readiness, ["complete", "needsReview"], `${path}.readiness`);
    const lines = expectArray(record.lines, parseStudentBillingMonthlyLine, `${path}.lines`);
    if (lines.length > 100)
        throw new Error(`${path}.lines exceeds limit`);
    const lessonSubtotal = integer(record.lessonSubtotal, `${path}.lessonSubtotal`);
    const eventSubtotal = integer(record.eventSubtotal, `${path}.eventSubtotal`);
    const manualSubtotal = integer(record.manualSubtotal, `${path}.manualSubtotal`);
    const totalAmount = integer(record.totalAmount, `${path}.totalAmount`);
    const warnings = expectArray(record.warnings, parseStudentBillingMonthlyWarning, `${path}.warnings`);
    if (lessonSubtotal + eventSubtotal + manualSubtotal !== totalAmount || lines.reduce((sum, line) => sum + line.amount, 0) !== totalAmount)
        throw new Error(`${path}.totals are invalid`);
    if ((readiness === "needsReview") !== warnings.some((warning) => warning.blocking))
        throw new Error(`${path}.readiness is inconsistent`);
    return {
        billingUnitId: parseStudentBillingIdentifier(record.billingUnitId, `${path}.billingUnitId`),
        tenantId: parseStudentBillingIdentifier(record.tenantId, `${path}.tenantId`),
        studentId: parseStudentBillingIdentifier(record.studentId, `${path}.studentId`),
        studentDisplayNameSnapshot: expectString(record.studentDisplayNameSnapshot, `${path}.studentDisplayNameSnapshot`),
        serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
        finalizationStatus: "draft",
        readiness,
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        lines,
        lessonSubtotal,
        eventSubtotal,
        manualSubtotal,
        totalAmount,
        warnings,
        payerPreview: parseStudentBillingMonthlyPayerPreview(record.payerPreview, `${path}.payerPreview`),
        sourceFingerprint: fingerprint(record.sourceFingerprint, `${path}.sourceFingerprint`),
        version: integer(record.version, `${path}.version`, 1),
        createdAt: iso(record.createdAt, `${path}.createdAt`),
        updatedAt: iso(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseStudentBillingMonthlyGeneration(value, path = "monthlyGeneration") {
    const record = expectRecord(value, path);
    exact(record, ["generationId", "tenantId", "serviceMonth", "sourceFingerprint", "version", "unitCount", "lineCount", "warningCount", "needsReviewCount", "globalWarnings", "generatedByPlatformUserId", "generatedAt"], path);
    return {
        generationId: parseStudentBillingIdentifier(record.generationId, `${path}.generationId`),
        tenantId: parseStudentBillingIdentifier(record.tenantId, `${path}.tenantId`),
        serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
        sourceFingerprint: fingerprint(record.sourceFingerprint, `${path}.sourceFingerprint`),
        version: integer(record.version, `${path}.version`, 1),
        unitCount: integer(record.unitCount, `${path}.unitCount`, 0),
        lineCount: integer(record.lineCount, `${path}.lineCount`, 0),
        warningCount: integer(record.warningCount, `${path}.warningCount`, 0),
        needsReviewCount: integer(record.needsReviewCount, `${path}.needsReviewCount`, 0),
        globalWarnings: expectArray(record.globalWarnings, parseStudentBillingMonthlyWarning, `${path}.globalWarnings`),
        generatedByPlatformUserId: parseStudentBillingIdentifier(record.generatedByPlatformUserId, `${path}.generatedByPlatformUserId`),
        generatedAt: iso(record.generatedAt, `${path}.generatedAt`)
    };
}
